import { api } from '../../shared/api/http'
import type { CompanyBrand } from '../branding/CompanyBranding'

export type Company = {
  branding?: CompanyBrand | null
  id: string
  legalName?: string
  tradeName: string
  maskedCnpj?: string
  address?: Record<string,string>
  pendingItems?: string[]
  currentUserRole: string
  hasScopedEventAccess?: boolean
  hasLogo: boolean
  canDelete?: boolean
}

export type Client = {
  companyId?: string
  canViewEvents?: boolean
  vigilanciaAllowed?: boolean
  portariaAllowed?: boolean
  id: string
  name: string
  maskedDocument?: string
  address?: Record<string,string>
  disabled: boolean
  hasLogo: boolean
}

export type Camera = {
  id: string
  name: string
  endpointDisplay: string
  connectionState: string
  isActive: boolean
  offlineWhatsAppEnabled?: boolean
  offlineWhatsAppDelayMinutes?: number
  offlineWhatsAppRepeatMinutes?: number
  offlineEventDelayMinutes?: number
  recordAudio?: boolean
  offlineEventRepeatMinutes?: number
  clientId?: string
  manufacturer?: string
  model?: string
  folderId?: string|null
}

export type CameraFolder={id:string;companyId:string;clientId:string;name:string}
export type ManagedRtmpChannel={id:string;displayName:string;publicCode:string;status:string;protocol:string}

export type LiveAccess = {
  whepUrl: string
  accessToken: string
  expiresAtUtc: string
  previewUrl?: string
  previewAccessToken?: string
}

export const listCompanies = () => api<Company[]>('/companies')
export const listClients = (companyId: string) =>
  api<Client[]>(`/companies/${companyId}/clients`)
export const listCameras = (companyId: string, clientId: string, signal?: AbortSignal) =>
  api<Camera[]>(`/companies/${companyId}/cameras?clientId=${encodeURIComponent(clientId)}`, { signal })
export const createLiveAccess = (companyId: string, cameraId: string, signal?: AbortSignal) =>
  api<LiveAccess>(`/companies/${companyId}/cameras/${cameraId}/live-access`, { method: 'POST', signal })
export const createPermanentLiveAccess = (cameraId: string) =>
  api<LiveAccess>(`/recordings/cameras/${cameraId}/live-access`, { method: 'POST' })
export const createPlaybackAccess = (companyId: string, camera: Camera, signal?: AbortSignal) =>
  createLiveAccess(companyId, camera.id, signal)
export const testCamera = (companyId: string, cameraId: string) =>
  api<Camera>(`/companies/${companyId}/cameras/${cameraId}/test`, { method: 'POST' })
export const setCameraActive = (companyId: string, cameraId: string, isActive: boolean) =>
  api<Camera>(`/companies/${companyId}/cameras/${cameraId}/active`, {
    method: 'PATCH', body: JSON.stringify({ isActive }),
  })
export const createCamera = (companyId: string, body: Record<string, unknown>) =>
  api<Camera>(`/companies/${companyId}/cameras`, { method: 'POST', body: JSON.stringify(body) })
export const createCameraPreviewAccess=(companyId:string,body:Record<string,unknown>)=>api<LiveAccess>(`/companies/${companyId}/cameras/preview-live-access`,{method:'POST',body:JSON.stringify(body)})
export const listAvailableRtmpChannels=(companyId:string,clientId:string)=>api<ManagedRtmpChannel[]>(`/companies/${companyId}/clients/${clientId}/rtmp-channels`)
export const createScopedRtmpPreviewAccess=(companyId:string,clientId:string,channelId:string)=>api<LiveAccess>(`/companies/${companyId}/clients/${clientId}/rtmp-channels/${channelId}/preview-access`,{method:'POST'})
export const renameCamera=(companyId:string,cameraId:string,name:string,offlineWhatsAppEnabled?:boolean,offlineWhatsAppDelayMinutes?:number,offlineWhatsAppRepeatMinutes?:number,offlineEventDelayMinutes?:number,offlineEventRepeatMinutes?:number,recordAudio?:boolean)=>api<Camera>(`/companies/${companyId}/cameras/${cameraId}`,{method:'PATCH',body:JSON.stringify({name,offlineWhatsAppEnabled,offlineWhatsAppDelayMinutes,offlineWhatsAppRepeatMinutes,offlineEventDelayMinutes,offlineEventRepeatMinutes,recordAudio})})
export const deleteCamera=(companyId:string,cameraId:string)=>api<null>(`/companies/${companyId}/cameras/${cameraId}`,{method:'DELETE'})
export const listCameraFolders=(companyId:string,clientId:string)=>api<CameraFolder[]>(`/companies/${companyId}/cameras/folders?clientId=${encodeURIComponent(clientId)}`)
export const createCameraFolder=(companyId:string,clientId:string,name:string)=>api<CameraFolder>(`/companies/${companyId}/cameras/folders`,{method:'POST',body:JSON.stringify({clientId,name})})
export const moveCameraToFolder=(companyId:string,clientId:string,cameraId:string,folderId:string|null)=>api<null>(`/companies/${companyId}/cameras/${cameraId}/folder?clientId=${encodeURIComponent(clientId)}`,{method:'PATCH',body:JSON.stringify({folderId})})
export const deleteCameraFolder=(companyId:string,clientId:string,folderId:string)=>api<null>(`/companies/${companyId}/cameras/folders/${folderId}?clientId=${encodeURIComponent(clientId)}`,{method:'DELETE'})
